import { apiFetch } from "./client";
import type {
  Attachment,
  AuditEntry,
  ChangelogEntry,
  Dashboard,
  Deployment,
  Funnel,
  FunnelAnalytics,
  FunnelKey,
  GpuType,
  MetricDefinition,
  MetricValue,
  Note,
  Opportunity,
  OpportunityDetail,
  PushResult,
  ReadyInputs,
  User,
} from "@/types/api";

const j = (body: unknown) => ({ body: JSON.stringify(body) });

export const auth = {
  setupRequired: () => apiFetch<{ setup_required: boolean }>("/auth/setup-required"),
  login: (username: string, password: string) =>
    apiFetch<{ access_token: string }>("/auth/login", { method: "POST", ...j({ username, password }) }),
  logout: () => apiFetch<{ ok: boolean }>("/auth/logout", { method: "POST" }),
  me: () => apiFetch<User>("/auth/me"),
};

export const funnels = {
  list: () => apiFetch<Funnel[]>("/funnels"),
  rename: (funnelId: string, name: string) =>
    apiFetch<Funnel>(`/funnels/${funnelId}`, { method: "PATCH", ...j({ name }) }),
  addStage: (funnelId: string, body: Record<string, unknown>) =>
    apiFetch(`/funnels/${funnelId}/stages`, { method: "POST", ...j(body) }),
  updateStage: (stageId: string, body: Record<string, unknown>) =>
    apiFetch(`/funnels/stages/${stageId}`, { method: "PATCH", ...j(body) }),
  reorderStages: (funnelId: string, stageIds: string[]) =>
    apiFetch(`/funnels/${funnelId}/stages/reorder`, { method: "POST", ...j({ stage_ids: stageIds }) }),
  archiveStage: (stageId: string) =>
    apiFetch(`/funnels/stages/${stageId}/archive`, { method: "POST" }),
  metricDefinitions: () => apiFetch<MetricDefinition[]>("/funnels/metric-definitions"),
  createMetricDefinition: (body: Record<string, unknown>) =>
    apiFetch<MetricDefinition>("/funnels/metric-definitions", { method: "POST", ...j(body) }),
  updateMetricDefinition: (id: string, body: Record<string, unknown>) =>
    apiFetch<MetricDefinition>(`/funnels/metric-definitions/${id}`, { method: "PATCH", ...j(body) }),
  archiveMetricDefinition: (id: string) =>
    apiFetch(`/funnels/metric-definitions/${id}/archive`, { method: "POST" }),
};

export interface OppFilter {
  funnel?: FunnelKey;
  funnel_id?: string;
  stage_id?: string;
  owner_user_id?: string;
  status?: string;
  include_archived?: boolean;
}

export const opportunities = {
  list: (f: OppFilter = {}) => {
    const q = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v != null && q.set(k, String(v)));
    const qs = q.toString();
    return apiFetch<Opportunity[]>(`/opportunities${qs ? `?${qs}` : ""}`);
  },
  get: (id: string) => apiFetch<OpportunityDetail>(`/opportunities/${id}`),
  create: (body: Record<string, unknown>) =>
    apiFetch<OpportunityDetail>("/opportunities", { method: "POST", ...j(body) }),
  update: (id: string, body: Record<string, unknown>) =>
    apiFetch<Opportunity>(`/opportunities/${id}`, { method: "PATCH", ...j(body) }),
  move: (id: string, toStageId: string) =>
    apiFetch<OpportunityDetail>(`/opportunities/${id}/move`, { method: "POST", ...j({ to_stage_id: toStageId }) }),
  archive: (id: string) => apiFetch(`/opportunities/${id}/archive`, { method: "POST" }),
  metrics: (id: string) => apiFetch<MetricValue[]>(`/opportunities/${id}/metrics`),
  setMetric: (id: string, metricDefinitionId: string, value: unknown) =>
    apiFetch<MetricValue>(`/opportunities/${id}/metrics`, {
      method: "PUT",
      ...j({ metric_definition_id: metricDefinitionId, value }),
    }),
  notes: (id: string) => apiFetch<Note[]>(`/opportunities/${id}/notes`),
  addNote: (id: string, body: string) =>
    apiFetch<Note>(`/opportunities/${id}/notes`, { method: "POST", ...j({ body }) }),
  attachments: (id: string) => apiFetch<Attachment[]>(`/opportunities/${id}/attachments`),
  uploadAttachment: (id: string, file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiFetch<Attachment>(`/opportunities/${id}/attachments`, { method: "POST", body: fd });
  },
  deleteAttachment: (attId: string) => apiFetch(`/attachments/${attId}`, { method: "DELETE" }),
};

export const deployments = {
  list: () => apiFetch<Deployment[]>("/deployments"),
  get: (id: string) => apiFetch<Deployment>(`/deployments/${id}`),
  create: (body: Record<string, unknown>) =>
    apiFetch<Deployment>("/deployments", { method: "POST", ...j(body) }),
  update: (id: string, body: Record<string, unknown>) =>
    apiFetch<Deployment>(`/deployments/${id}`, { method: "PATCH", ...j(body) }),
  gpuTypes: () => apiFetch<GpuType[]>("/deployments/gpu-types"),
  readyInputs: () => apiFetch<ReadyInputs>("/deployments/ready-inputs"),
  addAllocation: (id: string, opportunityId: string, amount: number, unit?: string) =>
    apiFetch<Deployment>(`/deployments/${id}/allocations`, {
      method: "POST",
      ...j({ opportunity_id: opportunityId, allocated_amount: amount, allocated_unit: unit }),
    }),
  removeAllocation: (id: string, allocId: string) =>
    apiFetch<Deployment>(`/deployments/${id}/allocations/${allocId}`, { method: "DELETE" }),
  push: (id: string) => apiFetch<PushResult>(`/deployments/${id}/push`, { method: "POST" }),
  unlink: (id: string) => apiFetch<Deployment>(`/deployments/${id}/unlink`, { method: "POST" }),
};

export const analytics = {
  dashboard: (from?: string, to?: string) => {
    const q = new URLSearchParams();
    if (from) q.set("date_from", from);
    if (to) q.set("date_to", to);
    const qs = q.toString();
    return apiFetch<Dashboard>(`/analytics/dashboard${qs ? `?${qs}` : ""}`);
  },
  funnel: (key: FunnelKey) => apiFetch<FunnelAnalytics>(`/analytics/funnels/${key}`),
};

export const changelog = {
  list: (limit = 100) => apiFetch<ChangelogEntry[]>(`/changelog?limit=${limit}`),
};

export const audit = {
  list: (entityType?: string, entityId?: string) => {
    const q = new URLSearchParams();
    if (entityType) q.set("entity_type", entityType);
    if (entityId) q.set("entity_id", entityId);
    const qs = q.toString();
    return apiFetch<AuditEntry[]>(`/audit${qs ? `?${qs}` : ""}`);
  },
};

export const exportCsv = {
  opportunities: (funnel?: FunnelKey) =>
    `/api/v1/export/opportunities.csv${funnel ? `?funnel=${funnel}` : ""}`,
  transitions: () => `/api/v1/export/transitions.csv`,
  deployments: () => `/api/v1/export/deployments.csv`,
};
